import { createApp } from 'vue'
import { createPinia } from 'pinia'
import VueSafeTeleport from 'vue-safe-teleport'
import { Quasar, Notify, Dialog, LocalStorage } from 'quasar'

import App from '@/App.vue'
import auth0Plugin from '@/setups/authSetup'
import router from './router'

import * as Sentry from '@sentry/vue'

import '@quasar/extras/material-icons/material-icons.css'
import '@quasar/extras/material-icons-outlined/material-icons-outlined.css'
import 'quasar/src/css/index.sass'
import axios, { type AxiosRequestConfig } from 'axios'
import tracker, { tracker as $tracker } from './tracker'
import LogRocket from 'logrocket'

import {
  Chart,
  LinearScale,
  Legend,
  PointElement,
  TimeScale,
  LineElement,
  BarElement,
  CategoryScale,
  Tooltip,
  Filler
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import annotationPlugin from 'chartjs-plugin-annotation'
import { PiniaColada } from '@pinia/colada'

Chart.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  Filler,
  annotationPlugin
)

const commit = {
  hash: import.meta.env.VITE_COMMIT_HASH || 'local-dev',
  timestamp: import.meta.env.VITE_COMMIT_TIMESTAMP || 'no-commit-timestamp'
}

console.log('Environment:', import.meta.env.MODE)
console.log({
  'Commit Hash:': commit.hash,
  'Commit Timestamp:': commit.timestamp
})
$tracker.version(commit.hash, commit.timestamp)

const app = createApp(App)

const setupApp = () => {
  if (import.meta.env.MODE !== 'reports') {
    app.use(auth0Plugin)
  }

  app.use(VueSafeTeleport)
  app.use(router)
  app.use(createPinia())
  app.use(PiniaColada, {
    queryOptions: {
      refetchOnMount: false, // We don't want to refetch on mount because we want to use the cached data
      refetchOnReconnect: false, // We don't want to refetch on reconnect because we want to use the cached data
      refetchOnWindowFocus: false // We don't want to refetch on window focus because we want to use the cached data
    }
  })
  app.use(tracker)

  if (import.meta.env.MODE === 'production' && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [Sentry.browserTracingIntegration({ router })],
      // Tracing
      tracesSampleRate: 1.0, // Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [/^https:\/\/api.hellodata\.ai/],
      release: 'frontend@' + commit.hash,
      environment: import.meta.env.MODE,
      beforeSend: (event) => {
        try {
          if (LogRocket) {
            Sentry.setContext('logrocket', {
              url: LogRocket.sessionURL
            })
          }
        } catch (error) {
          // TODO: remove the try/catch once we're sure the whole timing works well
          console.error('Error setting LogRocket context:', error)
        }
        return event
      }
    })
  }

  const preferDarkMode = LocalStorage.getItem<boolean | string>('prefer-dark')
  let dark = 'auto' as 'auto' | boolean
  if (preferDarkMode !== null) {
    dark = preferDarkMode === true || preferDarkMode === 'true'
  }

  app.use(Quasar, {
    plugins: { Notify, Dialog, LocalStorage },
    config: {
      dark
    }
  })

  app.mount('#app')
}

setupApp()

// add an interceptor to axios to detect 429 errors (rate limiting)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.error('Received a 401 Unauthorized error')
      await auth0Plugin.loginWithRedirect()
    } else if (error.response?.status === 403) {
      console.error('Received a 403 Forbidden error')
      await auth0Plugin.loginWithRedirect()
    } else if (error.response?.status === 429) {
      Notify.create({
        message: 'Too many requests, please try again in a minute',
        type: 'negative'
      })
    } else if (error.response?.status === 409) {
      return Promise.reject(
        new Error('Request was interrupted. A newer request replaced the previous one.')
      )
    } else if (error.response?.status === 500) {
      Notify.create({
        message:
          'An error occurred, please try again later, if the problem persists, please contact support',
        type: 'negative'
      })
    } else if (error.response?.status === 422) {
      // Doing this to not encapsulate the error in another error object.
      // This allows us to access the original error directly in the business logic.
      if (error instanceof Error) {
        return Promise.reject(error)
      }
    }
    return Promise.reject(new Error(error.response?.data?.message || 'Unknown error'))
  }
)

// Makes a request to the host to get the current version hash. If the version
// available is different from the current version that's running, then a toast
// will appear letting the user know that there is a new version available.
const checkForNewVersion = async function () {
  const config: AxiosRequestConfig = {
    headers: {
      Accept: 'text/plain'
    },
    responseType: 'text'
  }
  const response = await axios.get<string>('/version', config)

  // If the request fails, we can't determine if the versions are different.
  if (response.status !== 200) {
    return
  }

  // Trim to remove EOF and other white space characters from the response.
  const version = response.data.trim()

  if (commit.hash !== version) {
    $tracker.newVersionNotificationShown()
    Notify.create({
      message: 'A new version of Hello Data is available!',
      color: 'primary',
      actions: [
        {
          icon: 'refresh',
          color: 'white',
          noCaps: true,
          handler: () => {
            location.reload()
          }
        }
      ]
    })
  }
}

const twentyFourHours = 1000 * 60 * 60 * 24
setInterval(() => {
  void checkForNewVersion()
}, twentyFourHours)
